import React, { memo, useCallback, useEffect, useState } from 'react'
import styles from './ProductListPage.module.scss'
import { ApplicationRoutes } from '../../../routes';
import { useNavigate } from 'react-router-dom';
import ProductItem from './components/ProductItem';
import { useDispatch, useSelector } from 'react-redux';
import { RecommendationOptions, currentPage, loading, numberOfPages, option, productsToDisplay, recommendBudgetFitProducts, recommendPowerNeedsFitProducts, recommendProducts, recommendedAlready, selectedOption } from '../../../stores/slices/solar-recommendation-slice';
import { AppDispatch } from '../../../stores/store';
import NotFoundProductList from './components/NotFoundProductList';
import CommonPagination from '../../../common/components/pagination/CommonPagination';
import { toast } from 'react-toastify';
import CommonLoadingSpinner from '../../../common/components/loading-spinner/CommonLoadingSpinnerForPage';

type Props = {}

const ProductListPage: React.FC<Props> = memo((props: Props) => {
    const navigate = useNavigate();

    const [loadingFullPage, setLoadingFullPage] = useState<boolean>(false)

    const dispatch = useDispatch<AppDispatch>();

    const currentOption = useSelector(selectedOption);
    const products = useSelector(productsToDisplay);
    const page = useSelector(currentPage);
    const pageCount = useSelector(numberOfPages);
    const isLoading = useSelector(loading);

    const recommended = useSelector(recommendedAlready)

    const recommendFetch = useCallback(async () => {
        setLoadingFullPage(true)
        const result = await dispatch(recommendProducts({
            page: 1,
            pageSize: 6,
        }))

        if (!recommendProducts.fulfilled.match(result)) {
            toast.error('There was an error recommending the products');
        }
    }, [dispatch]);

    const returnToEditing = () => {
        navigate(`/${ApplicationRoutes.RECOMMENDATION_OVERVIEW}`);
    }

    const styleButtonSelected = (optionOfButton: RecommendationOptions) => {
        return `standardButton ${styles.tabButton} ${currentOption === optionOfButton ? styles.active : ''}`
    }

    const changeOption = (optionButton: RecommendationOptions) => {
        dispatch(option(optionButton))
    }

    useEffect(() => {
        if (!recommended) {
            recommendFetch().finally(() => setLoadingFullPage(false))
        }
    }, [recommendFetch, recommended])

    const onPageChange = useCallback(async (newPage: number) => {
        switch (currentOption) {
            case RecommendationOptions.BUDGET_FIT: {
                const result = await dispatch(recommendBudgetFitProducts({ page: newPage, pageSize: 6 }))

                if (!recommendBudgetFitProducts.fulfilled.match(result)) {
                    toast.error('There was an error fetching budget fit products');
                }
                break;
            }
            case RecommendationOptions.BEST_OFFER: {
                const result = await dispatch(recommendPowerNeedsFitProducts({ page: newPage, pageSize: 6 }))

                if (!recommendPowerNeedsFitProducts.fulfilled.match(result)) {
                    toast.error('There was an error fetching best offer products');
                }
                break;
            }
            default:
                break;
        }

    }, [currentOption, dispatch])

    return (
        <CommonLoadingSpinner loading={loadingFullPage}>
            <div className={styles.container}>
                <button className={`standardButton ${styles.backButton}`} onClick={returnToEditing}>
                    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M8.99984 12.3332L10.1665 11.1665L8.83317 9.83317H12.3332V8.1665H8.83317L10.1665 6.83317L8.99984 5.6665L5.6665 8.99984L8.99984 12.3332ZM8.99984 17.3332C7.84706 17.3332 6.76373 17.1144 5.74984 16.6769C4.73595 16.2394 3.854 15.6457 3.104 14.8957C2.354 14.1457 1.76025 13.2637 1.32275 12.2498C0.885254 11.2359 0.666504 10.1526 0.666504 8.99984C0.666504 7.84706 0.885254 6.76373 1.32275 5.74984C1.76025 4.73595 2.354 3.854 3.104 3.104C3.854 2.354 4.73595 1.76025 5.74984 1.32275C6.76373 0.885254 7.84706 0.666504 8.99984 0.666504C10.1526 0.666504 11.2359 0.885254 12.2498 1.32275C13.2637 1.76025 14.1457 2.354 14.8957 3.104C15.6457 3.854 16.2394 4.73595 16.6769 5.74984C17.1144 6.76373 17.3332 7.84706 17.3332 8.99984C17.3332 10.1526 17.1144 11.2359 16.6769 12.2498C16.2394 13.2637 15.6457 14.1457 14.8957 14.8957C14.1457 15.6457 13.2637 16.2394 12.2498 16.6769C11.2359 17.1144 10.1526 17.3332 8.99984 17.3332ZM8.99984 15.6665C10.8609 15.6665 12.4373 15.0207 13.729 13.729C15.0207 12.4373 15.6665 10.8609 15.6665 8.99984C15.6665 7.13873 15.0207 5.56234 13.729 4.27067C12.4373 2.979 10.8609 2.33317 8.99984 2.33317C7.13873 2.33317 5.56234 2.979 4.27067 4.27067C2.979 5.56234 2.33317 7.13873 2.33317 8.99984C2.33317 10.8609 2.979 12.4373 4.27067 13.729C5.56234 15.0207 7.13873 15.6665 8.99984 15.6665Z" fill="var(--app-primary-text)" />
                    </svg>
                    Go back
                </button>
                <div className={styles.tabContainer}>
                    <button
                        className={styleButtonSelected(RecommendationOptions.BEST_OFFER)}
                        onClick={() => changeOption(RecommendationOptions.BEST_OFFER)}
                    >Best Offer</button>
                    <button
                        className={styleButtonSelected(RecommendationOptions.BUDGET_FIT)}
                        onClick={() => changeOption(RecommendationOptions.BUDGET_FIT)}
                    >Budget fit</button>
                    <button
                        className={styleButtonSelected(RecommendationOptions.ENERGY_NEED)}
                        onClick={() => changeOption(RecommendationOptions.ENERGY_NEED)}
                    >Energy need</button>
                </div>
                {
                    products?.length === 0 ?
                        <NotFoundProductList /> :
                        <div className={styles.contentContainer}>
                            {
                                products?.map(product => <ProductItem key={product.id} product={product} />)
                            }
                        </div>
                }
                {
                    !!pageCount &&
                    !!page &&
                    <div className={styles.paginationContainer}>
                        <CommonPagination currentPage={page} numberOfPages={pageCount} isLoading={isLoading} onPageChange={onPageChange} />
                    </div>
                }

            </div>
        </CommonLoadingSpinner>
    )
})

export default ProductListPage